import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { FiSend, FiPhone, FiMail, FiMapPin, FiCheck, FiAlertCircle } from 'react-icons/fi';
import emailjs from '@emailjs/browser';

// Configuration EmailJS
const EMAILJS_SERVICE_ID = 'service_5a8t13o';
const EMAILJS_TEMPLATE_ID = 'template_i2aa6wr';
const EMAILJS_PUBLIC_KEY = 'zMoNd0pSIrPjf8bSK'; // À remplacer par votre clé publique EmailJS

// Initialiser EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const ContactForm = () => {
  // Form states
  const [formStatus, setFormStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'
  const [formStep, setFormStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  
  // React Hook Form
  const { 
    register, 
    handleSubmit, 
    formState: { errors, isValid },
    trigger,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      service: '',
      budget: '',
      message: '',
    }
  });

  // Form steps data
  const formSteps = [
    { name: 'Informations personnelles', fields: ['name', 'email', 'phone'] },
    { name: 'Détails du projet', fields: ['service', 'budget'] },
    { name: 'Message', fields: ['message'] },
  ];

  // Check if current step is valid
  const isStepValid = async () => {
    const result = await trigger(formSteps[formStep].fields);
    return result;
  };

  // Go to next step
  const nextStep = async () => {
    const isValid = await isStepValid();
    if (isValid) {
      setFormStep(prev => Math.min(prev + 1, formSteps.length - 1));
    }
  };

  // Go to previous step
  const prevStep = () => {
    setFormStep(prev => Math.max(prev - 1, 0));
  };

  // Handle form submission with EmailJS
  const onSubmit = async (data) => {
    setFormStatus('submitting');
    setErrorMessage('');
    
    console.log('Tentative d\'envoi du formulaire avec EmailJS:', data);
    
    try {
      // Préparer les données pour EmailJS
      const templateParams = {
        from_name: data.name,
        from_email: data.email,
        phone: data.phone || 'Non renseigné',
        service: data.service,
        budget: data.budget,
        message: data.message,
        to_email: 'contact@aiko-agency.fr' // Email de destination
      };
      
      console.log('Données à envoyer via EmailJS:', templateParams);
      
      // Envoi via EmailJS
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      console.log('Email envoyé avec succès:', result);
      setFormStatus('success');
      reset();
      setFormStep(0);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      setFormStatus('error');
      setErrorMessage('Une erreur est survenue lors de l\'envoi du formulaire. Veuillez réessayer plus tard.');
    }
  };

  // Calculate progress percentage
  const progressPercentage = ((formStep + 1) / formSteps.length) * 100;

  return (
    <section id="contact" className="py-24 bg-gradient-ocean text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-white/5 rounded-full filter blur-3xl"></div>
      </div>
      
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium mb-4">
              Contact
            </span>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Donnez vie à vos <span className="text-ocean-300">projets digitaux</span>
            </h2>
            
            <p className="text-white/80 text-lg mb-8">
              Prêt à transformer votre présence en ligne ? Contactez-nous pour discuter de votre projet 
              et obtenir un devis personnalisé.
            </p>
            
            {/* Contact Details */}
            <div className="space-y-6 mb-12">
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <FiPhone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-1">Téléphone</h3>
                  <p className="text-white/80">+33 1 23 45 67 89</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <FiMail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-1">Email</h3>
                  <p className="text-white/80">contact@breezy-agency.com</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <FiMapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-1">Adresse</h3>
                  <p className="text-white/80">123 Avenue du Digital<br />75001 Paris, France</p>
                </div>
              </div>
            </div>
            
            {/* Social Media */}
            <div>
              <h3 className="font-bold text-xl mb-4">Suivez-nous</h3>
              <div className="flex space-x-4">
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.48 7.56a.71.71 0 0 0-.51-.51 8.5 8.5 0 0 0-1.4-.17h-.08c-.24 0-.48-.05-.72-.05H9.23c-.24 0-.48.05-.72.05h-.08a8.5 8.5 0 0 0-1.4.17.71.71 0 0 0-.51.51A18.83 18.83 0 0 0 6.18 12a18.83 18.83 0 0 0 .34 4.44.71.71 0 0 0 .51.51 8.5 8.5 0 0 0 1.4.17h.08c.24 0 .48.05.72.05h5.54c.24 0 .48-.05.72-.05h.08a8.5 8.5 0 0 0 1.4-.17.71.71 0 0 0 .51-.51c.238-1.455.36-2.937.36-4.44a18.83 18.83 0 0 0-.36-4.44zM10.59 14.42V9.58l3.82 2.42-3.82 2.42z"/>
                  </svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z"/>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
          
          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
              {formStatus === 'success' ? (
                <motion.div 
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <FiCheck className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Message envoyé !</h3>
                  <p className="text-white/80 mb-6">
                    Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
                  </p>
                  <button 
                    onClick={() => setFormStatus('idle')}
                    className="btn btn-secondary"
                  >
                    Envoyer un nouveau message
                  </button>
                </motion.div>
              ) : formStatus === 'error' ? (
                <motion.div 
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                    <FiAlertCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Erreur</h3>
                  <p className="text-white/80 mb-6">
                    {errorMessage || "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer."}
                  </p>
                  <button 
                    onClick={() => setFormStatus('idle')}
                    className="btn btn-secondary"
                  >
                    Réessayer
                  </button>
                </motion.div>
              ) : (
                <>
                  {/* Form Header */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold mb-2">Demander un devis</h3>
                    <p className="text-white/80">
                      Remplissez le formulaire ci-dessous et nous vous contacterons rapidement.
                    </p>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full h-2 bg-white/20 rounded-full mb-8 overflow-hidden">
                    <motion.div 
                      className="h-full bg-ocean-300"
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  
                  {/* Step Indicator */}
                  <div className="flex justify-between mb-8">
                    {formSteps.map((step, index) => (
                      <div 
                        key={index} 
                        className="flex flex-col items-center"
                        onClick={() => {
                          if (index < formStep) {
                            setFormStep(index);
                          }
                        }}
                      >
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 cursor-pointer transition-colors ${
                            index < formStep 
                              ? 'bg-ocean-300 text-white' 
                              : index === formStep 
                                ? 'bg-white text-ocean-600 ring-2 ring-ocean-300' 
                                : 'bg-white/20 text-white'
                          }`}
                        >
                          {index < formStep ? <FiCheck /> : index + 1}
                        </div>
                        <span className="text-xs text-white/80">{step.name}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Form */}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Step 1: Personal Info */}
                    <div className={formStep === 0 ? 'block' : 'hidden'}>
                      <div className="mb-4">
                        <label htmlFor="name" className="block text-white mb-2">Nom complet *</label>
                        <input
                          id="name"
                          type="text"
                          className={`input-field ${errors.name ? 'border-red-500' : ''}`}
                          placeholder="Votre nom"
                          {...register('name', { required: 'Le nom est requis' })}
                        />
                        {errors.name && (
                          <p className="text-red-400 text-sm mt-1 flex items-center">
                            <FiAlertCircle className="mr-1" /> {errors.name.message}
                          </p>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="email" className="block text-white mb-2">Email *</label>
                        <input
                          id="email"
                          type="email"
                          className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                          placeholder="votre@email.com"
                          {...register('email', { 
                            required: 'L\'email est requis',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Adresse email invalide'
                            }
                          })}
                        />
                        {errors.email && (
                          <p className="text-red-400 text-sm mt-1 flex items-center">
                            <FiAlertCircle className="mr-1" /> {errors.email.message}
                          </p>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="phone" className="block text-white mb-2">Téléphone</label>
                        <input
                          id="phone"
                          type="tel"
                          className={`input-field ${errors.phone ? 'border-red-500' : ''}`}
                          placeholder="Votre numéro de téléphone"
                          {...register('phone', { 
                            pattern: {
                              value: /^[0-9+\s()-]{8,15}$/,
                              message: 'Numéro de téléphone invalide'
                            }
                          })}
                        />
                        {errors.phone && (
                          <p className="text-red-400 text-sm mt-1 flex items-center">
                            <FiAlertCircle className="mr-1" /> {errors.phone.message}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Step 2: Project Details */}
                    <div className={formStep === 1 ? 'block' : 'hidden'}>
                      <div className="mb-4">
                        <label htmlFor="service" className="block text-white mb-2">Service souhaité *</label>
                        <select
                          id="service"
                          className={`input-field ${errors.service ? 'border-red-500' : ''}`}
                          {...register('service', { required: 'Veuillez sélectionner un service' })}
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="site-web">Création de Site Web</option>
                          <option value="design-ui-ux">Design UI/UX</option>
                          <option value="identite-visuelle">Identité Visuelle</option>
                          <option value="e-commerce">E-commerce</option>
                          <option value="seo">Référencement SEO</option>
                          <option value="marketing">Marketing Digital</option>
                        </select>
                        {errors.service && (
                          <p className="text-red-400 text-sm mt-1 flex items-center">
                            <FiAlertCircle className="mr-1" /> {errors.service.message}
                          </p>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="budget" className="block text-white mb-2">Budget estimé *</label>
                        <select
                          id="budget"
                          className={`input-field ${errors.budget ? 'border-red-500' : ''}`}
                          {...register('budget', { required: 'Veuillez sélectionner un budget' })}
                        >
                          <option value="">Sélectionnez votre budget</option>
                          <option value="moins-1000">Moins de 1 000 €</option>
                          <option value="1000-3000">1 000 € - 3 000 €</option>
                          <option value="3000-5000">3 000 € - 5 000 €</option>
                          <option value="5000-10000">5 000 € - 10 000 €</option>
                          <option value="plus-10000">Plus de 10 000 €</option>
                        </select>
                        {errors.budget && (
                          <p className="text-red-400 text-sm mt-1 flex items-center">
                            <FiAlertCircle className="mr-1" /> {errors.budget.message}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Step 3: Message */}
                    <div className={formStep === 2 ? 'block' : 'hidden'}>
                      <div className="mb-4">
                        <label htmlFor="message" className="block text-white mb-2">Description du projet</label>
                        <textarea
                          id="message"
                          rows={5}
                          className="input-field"
                          placeholder="Décrivez votre projet, vos objectifs, délais, etc."
                          {...register('message')}
                        />
                      </div>
                      
                      <div className="mb-4">
                        <label className="flex items-start">
                          <input
                            type="checkbox"
                            className="mt-1 mr-2"
                            required
                          />
                          <span className="text-white/80 text-sm">
                            J'accepte que mes données soient traitées conformément à la politique de confidentialité *
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8">
                      {formStep > 0 && (
                        <button
                          type="button"
                          onClick={prevStep}
                          className="btn btn-secondary"
                        >
                          Précédent
                        </button>
                      )}
                      
                      {formStep < formSteps.length - 1 ? (
                        <button
                          type="button"
                          onClick={nextStep}
                          className="btn btn-primary ml-auto"
                          disabled={!isValid}
                        >
                          Suivant
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="btn btn-primary ml-auto"
                          disabled={formStatus === 'submitting'}
                        >
                          {formStatus === 'submitting' ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Envoi en cours...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              Envoyer
                              <FiSend className="ml-2" />
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
